/**
 * 「这个人叫什么」—— 前端侧的显示名规则，和后端 `src/utils/displayName.js` 保持一致。
 *
 * 背景：注册时后端把 `username` 直接写成了邮箱，所以任何直接拿 `username` 当人名
 * 用的地方，显示出来的都是一串邮箱。后端的查询已经统一改成返回显示名了，这里管的
 * 是前端自己从 localStorage 拼名字的那几处。
 *
 * 优先级：姓+名 → 全名 → 真正的用户名（不等于邮箱时）→ 邮箱
 */

export interface NameSource {
  first_name?: string | null
  last_name?: string | null
  full_name?: string | null
  username?: string | null
  email?: string | null
}

export function displayNameOf(user: NameSource | null | undefined, fallback = 'User'): string {
  if (!user) return fallback

  const full = [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
  if (full) return full

  if (user.full_name && user.full_name.trim()) return user.full_name.trim()

  const email = user.email || ''
  // username 等于邮箱 = 注册时塞进去的占位，不算名字
  if (user.username && user.username.toLowerCase() !== email.toLowerCase()) return user.username

  return email || user.username || fallback
}

/** 当前登录用户的显示名（从 localStorage 读，登录/刷新时由 /auth/me 写入） */
export function currentUserDisplayName(fallback = 'User'): string {
  try {
    const raw = localStorage.getItem('user')
    if (raw) return displayNameOf(JSON.parse(raw) as NameSource, fallback)
  } catch { /* 解析不了就当没有 */ }
  return fallback
}

/** 头像里的两个字母缩写，"Jay Wang" → "JW" */
export function initialsOf(name: string): string {
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}
